
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PricingTiers() {
  const [animated, setAnimated] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimated(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleJoinWaitlist = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('final-cta');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const tiers = [
    {
      name: 'FOUNDER',
      badge: '🏆 500 spots',
      price: '$25',
      period: '/month',
      billing: 'Billed $300/year',
      features: [
        'Unlimited pleadings and contracts',
        'Voice dictation from anywhere',
        'Court-specific templates',
        'All jurisdictions',
        'Priority support',
        'Founder badge',
        'Rate locked forever',
      ],
      note: 'Only available during founder launch. Once 500 spots fill, this tier closes permanently',
      cta: 'Claim Founder Spot',
      highlight: true,
      limited: true,
    },
    {
      name: 'PRE-LAUNCH',
      badge: 'Available until public launch',
      price: '$79',
      period: '/month',
      billing: 'Cancel anytime',
      features: [
        'Unlimited pleadings and contracts',
        'Voice dictation from anywhere',
        'Court-specific templates',
        'All jurisdictions',
        'Standard support',
      ],
      note: 'Price increases to $149/month at public launch',
      cta: 'Join Pre-Launch',
      highlight: false,
      limited: false,
    },
    {
      name: 'STANDARD',
      badge: 'Coming at public launch',
      price: '$149',
      period: '/month',
      billing: 'Cancel anytime',
      features: [
        'Unlimited pleadings and contracts',
        'Voice dictation from anywhere',
        'Court-specific templates',
        'All jurisdictions',
        'Standard support',
      ],
      note: 'This is the regular price. Lock in lower rates now',
      cta: 'Join Waitlist',
      highlight: false,
      limited: false,
    },
  ];

  return (
    <section ref={sectionRef} id="pricing" className="relative py-16 sm:py-20 lg:py-32 bg-[#09090b]">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-6 lg:gap-8">
          {tiers.map((tier, index) => (
            <div
              key={index}
              className={`relative p-6 sm:p-6 lg:p-8 rounded-2xl transition-all duration-700 flex flex-col ${
                tier.highlight
                  ? 'bg-gradient-to-b from-[#d4af37]/20 to-[#0f0f11] border-2 border-[#d4af37] shadow-[0_0_60px_rgba(212,175,55,0.3)]'
                  : 'bg-[#0f0f11] border border-[#1a1a1c]'
              } ${animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {tier.limited && (
                <div className="absolute -top-3 sm:-top-4 left-1/2 -translate-x-1/2 px-3 sm:px-4 py-1.5 sm:py-2 bg-[#d4af37] text-black text-xs sm:text-sm font-black rounded-full whitespace-nowrap">
                  LIMITED
                </div>
              )}

              <div className="space-y-4 sm:space-y-6 flex-grow flex flex-col">
                <div>
                  <h3 className={`text-xl sm:text-2xl lg:text-3xl font-black mb-2 ${tier.highlight ? 'text-[#d4af37]' : 'text-white'}`}>
                    {tier.name}
                  </h3>
                  <p className="text-[#a1a1aa] text-xs sm:text-sm font-semibold">{tier.badge}</p>
                </div>

                <div>
                  <div className="flex items-baseline gap-2">
                    <span className={`text-4xl sm:text-5xl lg:text-6xl font-black ${tier.highlight ? 'text-[#d4af37]' : 'text-white'}`}>
                      {tier.price}
                    </span>
                    <span className="text-[#a1a1aa] text-lg sm:text-xl">{tier.period}</span>
                  </div>
                  <p className="text-[#a1a1aa] text-xs sm:text-sm mt-2">{tier.billing}</p>
                </div>

                <div className="space-y-2.5 sm:space-y-3 py-4 sm:py-6 border-t border-b border-[#27272a]">
                  {tier.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2.5 sm:gap-3">
                      <span className={`text-base sm:text-lg mt-0.5 ${tier.highlight ? 'text-[#d4af37]' : 'text-[#06b6d4]'}`}>✓</span>
                      <span className="text-[#d4d4d8] text-sm sm:text-base">{feature}</span>
                    </div>
                  ))}
                </div>

                <p className="text-[#a1a1aa] text-xs sm:text-sm italic leading-relaxed min-h-[50px] sm:min-h-[60px]">
                  {tier.note}
                </p>

                <div className="mt-auto pt-4">
                  <button
                    onClick={handleJoinWaitlist}
                    className={`w-full px-4 sm:px-6 py-3 sm:py-4 rounded-full font-bold text-sm sm:text-base transition-all cursor-pointer whitespace-nowrap ${
                      tier.highlight
                        ? 'bg-[#d4af37] text-black hover:shadow-[0_0_40px_rgba(212,175,55,0.6)]'
                        : 'bg-[#06b6d4] text-white hover:shadow-[0_0_40px_rgba(6,182,212,0.4)]'
                    }`}
                  >
                    {tier.cta}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
